/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { OrganizationBookingSettingsResponse } from "../models/OrganizationBookingSettingsResponse";
import type { OrganizationWebPagePublicResponse } from "../models/OrganizationWebPagePublicResponse";
import type { OrganizationWebPageUpdate } from "../models/OrganizationWebPageUpdate";
import type { TutorBookingPageResponse } from "../models/TutorBookingPageResponse";
import type { TutorWebPageResponse } from "../models/TutorWebPageResponse";
import type { TutorWebPageUpdate } from "../models/TutorWebPageUpdate";
import type { VerifyCheck } from "../models/VerifyCheck";
import type { VerifyCheckResponse } from "../models/VerifyCheckResponse";
import type { VerifyStart } from "../models/VerifyStart";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class WebPageService {
  /**
   * Get Organization Web Page
   * An organization's public booking page and its booking rules.
   * @returns OrganizationWebPagePublicResponse Successful Response
   * @throws ApiError
   */
  public static getOrganizationWebPage({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<OrganizationWebPagePublicResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/web_page/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Organization Web Page By Org Id
   * Save the Booking pages tab: the online booking switch, the page, the rules
   * and which subjects and places families see, in one transaction.
   * @returns OrganizationBookingSettingsResponse Successful Response
   * @throws ApiError
   */
  public static updateOrganizationWebPageByOrgId({
    orgId,
    requestBody,
  }: {
    orgId: number;
    requestBody: OrganizationWebPageUpdate;
  }): CancelablePromise<OrganizationBookingSettingsResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/web_page/organizations/{org_id}",
      path: {
        org_id: orgId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Organization Web Page By Custom Domain
   * The public booking page of the org whose custom domain the page is on.
   * @returns OrganizationWebPagePublicResponse Successful Response
   * @throws ApiError
   */
  public static getOrganizationWebPageByCustomDomain(): CancelablePromise<OrganizationWebPagePublicResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/web_page/organization-web-page",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Start Booking Verification
   * Text a booking code to a phone, within the throttle.
   * @returns void
   * @throws ApiError
   */
  public static startBookingVerification({
    requestBody,
  }: {
    requestBody: VerifyStart;
  }): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/web_page/verify/start",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Check Booking Verification
   * Check a booking code; returns the booking token and the family.
   * @returns VerifyCheckResponse Successful Response
   * @throws ApiError
   */
  public static checkBookingVerification({
    requestBody,
  }: {
    requestBody: VerifyCheck;
  }): CancelablePromise<VerifyCheckResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/web_page/verify/check",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Organization Booking Settings
   * The Booking pages tab: settings, subjects and places, for admins.
   * @returns OrganizationBookingSettingsResponse Successful Response
   * @throws ApiError
   */
  public static getOrganizationBookingSettings({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<OrganizationBookingSettingsResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/web_page/organizations/{org_id}/settings",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Tutor Web Page Settings
   * A tutor's booking page for its editors, readable while booking is off.
   * @returns TutorBookingPageResponse Successful Response
   * @throws ApiError
   */
  public static getTutorWebPageSettings({
    tutorId,
  }: {
    tutorId: number;
  }): CancelablePromise<TutorBookingPageResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/web_page/tutors/{tutor_id}/settings",
      path: {
        tutor_id: tutorId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Tutor Web Page
   * Get tutor web page for a specified tutor from the database.
   * @returns TutorWebPageResponse Successful Response
   * @throws ApiError
   */
  public static getTutorWebPage({
    tutorId,
  }: {
    tutorId: number;
  }): CancelablePromise<TutorWebPageResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/web_page/tutors/{tutor_id}",
      path: {
        tutor_id: tutorId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Tutor Web Page By Tutor Id
   * Update a tutor's booking page: admins for any tutor, tutors for their own.
   * @returns TutorWebPageResponse Successful Response
   * @throws ApiError
   */
  public static updateTutorWebPageByTutorId({
    tutorId,
    requestBody,
  }: {
    tutorId: number;
    requestBody: TutorWebPageUpdate;
  }): CancelablePromise<TutorWebPageResponse> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/web_page/tutors/{tutor_id}",
      path: {
        tutor_id: tutorId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Tutor Web Page By Slug
   * A tutor's page on a white-label domain, by the slug of their name.
   * @returns TutorWebPageResponse Successful Response
   * @throws ApiError
   */
  public static getTutorWebPageBySlug({
    tutorSlug,
  }: {
    tutorSlug: string;
  }): CancelablePromise<TutorWebPageResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/web_page/{tutor_slug}",
      path: {
        tutor_slug: tutorSlug,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
